import { defineStore } from "pinia";
import axios from "axios";
import { computed, ref, nextTick } from "vue";
import { API_URL } from "@/utils/constans";
import { useRequestsStore } from "@/stores/storeRequests";

export const useQuizStore = defineStore("quiz", () => {
	const storeRequests = useRequestsStore();

	const words1 = ["ШЕДЕВР", "ГАЛЕРЕЯ", "ВЕРНИСАЖ", "ЭКСПОЗИЦИЯ", "КОЛЛЕКЦИЯ", "ПОЛОТНО", "ХОЛСТ", "ПАЛИТРА", "МУЗА", "ЭСТЕТИКА", "ВДОХНОВЕНИЕ", "КУРАТОР", "ЭКСПОНАТ", "РЕТРОСПЕКТИВА", "СИМВОЛИЗМ", "АВАНГАРД", "ИМПРЕССИОНИЗМ", "СЮЖЕТ", "ФОН", "ПЕРСПЕКТИВА"];
	const words2 = ["КИСТЬ", "МАСТЕР", "КРАСКА", "МАЗОК", "ЭСКИЗ", "ЭТЮД", "НАТЮРМОРТ", "ПЕЙЗАЖ", "ПОРТРЕТ", "РИСУНОК", "ТВОРЕЦ", "ЗАМЫСЕЛ", "ВОПЛОЩЕНИЕ", "ПРОФИЛЬ", "РАКУРС", "ХОЛСТ", "БУМАГА", "АКВАРЕЛЬ", "МОЛЬБЕРТ", "ТЕМПЕРА"];
	const words3 = ["ГРЕЧКА", "ОВСЯНКА", "ПШЕНО", "РИС", "ПЕРЛОВКА", "ЯЧМЕНЬ", "ЧЕЧЕВИЦА", "КУКУРУЗА", "МАНКА", "БУЛГУР", "КИНОА", "ПОЛБА", "АМАРАНТ", "СОЯ", "НУТ", "ГОРОХ", "КУСКУС", "ПШЕНО", "ОВЕС", "РОЖЬ"];
	const words4 = ["РЕПИН", "СЕРОВ", "ВРУБЕЛЬ", "ШИШКИН", "ЛЕВИТАН", "АЙВАЗОВСКИЙ", "ВАСНЕЦОВ", "КУСТОДИЕВ", "МАЛЕВИЧ", "АРХИПОВ", "РЕРИХ", "РУБЛЕВ", "НЕСТЕРОВ", "БРЮЛЛОВ", "ИВАНОВ", "ШАГАЛ", "КРАМСКОЙ", "ФЕДОТОВ", "САВРАСОВ", "СУРИКОВ"];
	const words = [words1, words2, words3, words4];
	const topicWords = ["миром искусства", "процессом творчества", "крупой", "художниками"];
	const themes = ["art", "creative_process", "cereals", "artists"];

	const randomWords = ref([]);
	const currentTopicWords = ref(topicWords[0]);
	const currentTheme = ref(themes[0]);
	const quizData = ref({});
	const isLoaderQuiz = ref(false);
	const isShowWelcomScreen = ref(true);
	const isShowGame = ref(false);
	const isShowEndGame = ref(false);
	const isShowResultGame = ref(false);

	const grid = ref([]);
	const size = 13;
	const highlighted = ref([]);
	const foundHighlights = ref([]);
	const selectedCells = ref([]);
	const foundWords = ref([]);
	const solutionCoords = ref([]);
	const firstClick = ref(null);
	const isSelecting = ref(false);

	const remainingTime = ref({ minutes: "3", secundes: "00" });
	let intervalId;

	// =======================
	// Вспомогательные функции
	// =======================
	function getRandomTenWords(list) {
		const unique = Array.from(new Set(list));
		for (let i = unique.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[unique[i], unique[j]] = [unique[j], unique[i]];
		}
		return unique.slice(0, 10);
	}

	const getRandomWords = () => {
		const randomIndex = Math.floor(Math.random() * words.length);
		randomWords.value = getRandomTenWords(words[randomIndex]);
		currentTopicWords.value = topicWords[randomIndex];
		currentTheme.value = themes[randomIndex];
	};

	const initHighlight = () => {
		highlighted.value = grid.value.map((row) => row.map(() => false));
	};

	const initFoundHighlights = () => {
		foundHighlights.value = grid.value.map((row) => row.map(() => false));
	};

	const highlightWords = () => {
		if (!window.wordfind) return;
		const solution = window.wordfind.solve(grid.value, randomWords.value);
		initHighlight();
		if (!solution || !solution.found) return;

		solution.found.forEach((item) => {
			const { word, x, y, orientation } = item;
			for (let i = 0; i < word.length; i++) {
				let row = y,
					col = x;
				switch (orientation) {
					case "horizontal":
						col += i;
						break;
					case "horizontalBack":
						col -= i;
						break;
					case "vertical":
						row += i;
						break;
					case "verticalUp":
						row -= i;
						break;
				}
				if (highlighted.value[row] && highlighted.value[row][col] !== undefined) {
					highlighted.value[row][col] = true;
				}
			}
		});
	};

	const gridStyle = computed(() => ({
		display: "grid",
		gridTemplateColumns: `repeat(${size}, 4rem)`,
		gridTemplateRows: `repeat(${size}, 4rem)`,
	}));

	const flatGrid = computed(() => grid.value.flat());

	const resetTimer = () => {
		clearInterval(intervalId);
		remainingTime.value = { minutes: "03", secundes: "00" };
	};

	const timer = () => {
		let totalSeconds = 60 * 3;
		intervalId = setInterval(() => {
			const minutes = Math.floor(totalSeconds / 60);
			const seconds = totalSeconds % 60;
			remainingTime.value.minutes = String(minutes).padStart(2, "0");
			remainingTime.value.secundes = String(seconds).padStart(2, "0");
			if (totalSeconds <= 0) {
				endGame(false);
			} else totalSeconds--;
		}, 1000);
	};

	const generatePuzzle = () => {
		if (!window.wordfind) return;
		resetTimer();
		getRandomWords();
		timer();
		const puzzle = window.wordfind.newPuzzle(randomWords.value, {
			height: size,
			width: size,
			orientations: ["horizontal", "horizontalBack", "vertical", "verticalUp"],
		});
		grid.value = puzzle.map((row) => (Array.isArray(row) ? row : row.split("")));
		highlightWords();
		solutionCoords.value = window.wordfind.solve(grid.value, randomWords.value)?.found || [];
		initFoundHighlights();
		selectedCells.value = [];
		firstClick.value = null;
		foundWords.value = [];
	};

	// =======================
	// Игровые функции
	// =======================
	const startGame = async () => {
		isLoaderQuiz.value = true;
		isShowWelcomScreen.value = false;
		isShowGame.value = true;
		isShowEndGame.value = false;
		isShowResultGame.value = false;
		generatePuzzle();
		await nextTick();
		window.scrollTo({ top: 0, behavior: "smooth" });

		try {
			quizData.value = await storeRequests.startGameFillWords({ theme: currentTheme.value });
		} catch (err) {
			console.error(err);
		} finally {
			isLoaderQuiz.value = false;
		}
	};

	const restartGame = () => {
		quizData.value = {};
		isShowWelcomScreen.value = true;
		isShowGame.value = false;
		isShowEndGame.value = false;
		isShowResultGame.value = false;
		localStorage.removeItem("tokenGameFillword");
	};

	const endGame = async (mode) => {
		clearInterval(intervalId);
		isShowGame.value = false;
		isShowEndGame.value = true;
		isShowResultGame.value = mode;
		if (mode) localStorage.setItem("tokenGameFillword", quizData.value.uuid);
		try {
			await axios.patch(`${API_URL}/api/quiz_fillword/finish?uuid=${quizData.value.uuid}`, {
				points: foundWords.value.length,
			});
		} catch (err) {
			console.error(err);
		}
	};

	// =======================
	// Выбор клеток
	// =======================
	function selectRange(start, end) {
		const cells = [];
		if (start.row === end.row) {
			const row = start.row;
			for (let c = Math.min(start.col, end.col); c <= Math.max(start.col, end.col); c++) {
				cells.push({ row, col: c });
			}
		} else if (start.col === end.col) {
			const col = start.col;
			for (let r = Math.min(start.row, end.row); r <= Math.max(start.row, end.row); r++) {
				cells.push({ row: r, col });
			}
		}
		selectedCells.value = cells;
	}

	// helper: возвращает массив координат типа "r,c" для диапазона между start и end (включительно)
	function buildRangeCoords(start, end) {
		const coords = [];
		if (start.row === end.row) {
			const row = start.row;
			const from = Math.min(start.col, end.col);
			const to = Math.max(start.col, end.col);
			for (let c = from; c <= to; c++) coords.push(`${row},${c}`);
		} else if (start.col === end.col) {
			const col = start.col;
			const from = Math.min(start.row, end.row);
			const to = Math.max(start.row, end.row);
			for (let r = from; r <= to; r++) coords.push(`${r},${col}`);
		}
		return coords;
	}

	// helper: проверяет, совпадает ли диапазон с координатами любого слова из solutionCoords
	function rangeMatchesAnyWord(start, end) {
		const rangeCoords = buildRangeCoords(start, end);
		if (!rangeCoords.length) return false;

		// перебираем все найденные слова из solutionCoords
		for (const wordObj of solutionCoords.value) {
			// строим координаты для этого слова по ориентации
			const wordCoords = [];
			for (let i = 0; i < wordObj.word.length; i++) {
				let r = wordObj.y;
				let c = wordObj.x;
				switch (wordObj.orientation) {
					case "horizontal":
						c += i;
						break;
					case "horizontalBack":
						c -= i;
						break;
					case "vertical":
						r += i;
						break;
					case "verticalUp":
						r -= i;
						break;
					default:
						break;
				}
				wordCoords.push(`${r},${c}`);
			}

			// сравниваем точное совпадение по порядку ИЛИ по обратному порядку
			const wordCoordsStr = wordCoords.join();
			const wordCoordsRevStr = [...wordCoords].reverse().join();
			const rangeStr = rangeCoords.join();

			if (rangeStr === wordCoordsStr || rangeStr === wordCoordsRevStr) {
				return { ok: true, wordObj }; // вернём информацию о совпавшем слове
			}
		}

		return { ok: false };
	}

	// helper: формирует массив клеток между start и end (включительно) только если они на одной линии
	function buildLineCells(start, end) {
		const cells = [];
		if (start.row === end.row) {
			const row = start.row;
			const from = Math.min(start.col, end.col);
			const to = Math.max(start.col, end.col);
			for (let c = from; c <= to; c++) cells.push({ row, col: c });
		} else if (start.col === end.col) {
			const col = start.col;
			const from = Math.min(start.row, end.row);
			const to = Math.max(start.row, end.row);
			for (let r = from; r <= to; r++) cells.push({ row: r, col });
		}
		return cells;
	}

	
	function markErrorRange(start, end, delayMs = 300) {
		const cells = buildLineCells(start, end);
		if (!cells.length) return;

		// помечаем ошибочные клетки
		selectedCells.value = cells.map((c) => ({ ...c, error: true }));

		// через delayMs снимаем ошибку (и очищаем выделение)
		setTimeout(() => {
			// если в промежутке с момента установки ошибок пользователь не подтвердил слово,
			// мы сбрасываем выделение / ошибки.
			selectedCells.value = [];
		}, delayMs);
	}

	function handleCellClick(row, col) {
		// Если нет первой точки — запоминаем
		if (!firstClick.value) {
			firstClick.value = { row, col };
			selectedCells.value = [{ row, col }]; // показываем одиночную выбранную клетку
			return;
		}

		const start = firstClick.value;
		const end = { row, col };

		const sameRow = start.row === end.row;
		const sameCol = start.col === end.col;

		// Если не на одной линии — оставляем только текущую (как новая первая точка)
		if (!sameRow && !sameCol) {
			firstClick.value = { row, col };
			selectedCells.value = [{ row, col }];
			return;
		}

		// Строим диапазон по линии
		const cells = buildLineCells(start, end);
		if (!cells.length) {
			// на случай, если что-то пошло не так — сбросить
			firstClick.value = null;
			selectedCells.value = [];
			return;
		}

		// Формируем строку выбранного диапазона и проверяем в обе стороны
		const wordStr = cells.map((c) => grid.value[c.row][c.col]).join("");
		const reversed = wordStr.split("").reverse().join("");
		const matched = randomWords.value.find((w) => w === wordStr || w === reversed);

		if (matched) {
			// Правильный диапазон — выделяем и фиксируем
			selectedCells.value = cells; // показываем выделение
			// finalizeSelection обработает подсветку и сброс firstClick
			finalizeSelection();
		} else {
			// Неверный диапазон — ставим --error и снимаем через 0.3s
			markErrorRange(start, end, 500);
		}

		// сброс firstClick (в любом случае)
		firstClick.value = null;
	}

	function finalizeSelection() {
		if (!selectedCells.value.length) return;

		// Если selectedCells содержит объект с флагом error — игнорируем
		if (selectedCells.value.some((c) => c.error)) {
			// не принимаем в качестве слова, просто очищаем
			selectedCells.value = [];
			firstClick.value = null;
			return;
		}

		const wordStr = selectedCells.value.map((c) => grid.value[c.row][c.col]).join("");
		const reversed = wordStr.split("").reverse().join("");
		const matched = randomWords.value.find((w) => w === wordStr || w === reversed);

		if (matched && !foundWords.value.includes(matched)) {
			foundWords.value.push(matched);
			selectedCells.value.forEach((c) => {
				if (foundHighlights.value[c.row] && foundHighlights.value[c.row][c.col] !== undefined) {
					foundHighlights.value[c.row][c.col] = true;
				}
			});
		}

		selectedCells.value = [];
		firstClick.value = null;

		if (foundWords.value.length === randomWords.value.length) endGame(true);
	}

	return {
		generatePuzzle,
		highlightWords,
		startGame,
		restartGame,

		handleCellClick,
		finalizeSelection,

		isLoaderQuiz,
		quizData,
		isShowWelcomScreen,
		isShowGame,
		isShowEndGame,
		isShowResultGame,
		flatGrid,
		randomWords,
		currentTopicWords,
		remainingTime,
		gridStyle,
		highlighted,
		size,
		timerActive: ref(true),

		selectedCells,
		foundWords,
		foundHighlights,
		isSelecting,
	};
});
